import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, CheckCircle, Sparkle, ArrowRight, X, Upload, Warning, FileText, Stack, FlowArrow, Gavel } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/glass/GlassCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable picker: browse built-in packs, preview diff, commit.
 * Used in Onboarding step 4 AND standalone from /app/global-admin.
 *
 * onImported(result) — called with the backend response after a successful commit.
 * allowSkip — if true, renders a "Skip for now" ghost button.
 * compact — tighter layout for use inside wizards.
 */
const SLICE_KEYS = ["presets", "workflows", "boilerplates", "mappings"];

export default function StarterPackPicker({ onImported, allowSkip = true, onSkip, compact = false }) {
  const [packs, setPacks] = useState(null);
  const [diff, setDiff] = useState(null);   // { pack, diff }
  const [committing, setCommitting] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  // Slice toggles for pick-and-choose imports — default all enabled
  const [slices, setSlices] = useState({ presets: true, workflows: true, boilerplates: true, mappings: true });

  useEffect(() => {
    api.get("/starter-packs").then(({ data }) => setPacks(data.packs || [])).catch(() => setPacks([]));
  }, []);

  const preview = async (pack) => {
    try {
      // Reset slice toggles for each new preview so the dialog is predictable
      setSlices({ presets: true, workflows: true, boilerplates: true, mappings: true });
      const { data } = await api.post("/starter-packs/import", { pack_id: pack.id, dry_run: true });
      setDiff({ pack, diff: data.diff });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not preview pack"); }
  };

  const commit = async () => {
    if (!diff) return;
    const chosen = SLICE_KEYS.filter((k) => slices[k]);
    if (chosen.length === 0) { toast.error("Pick at least one slice to import"); return; }
    setCommitting(true);
    try {
      const { data } = await api.post("/starter-packs/import", {
        pack_id: diff.pack.id,
        slices: chosen,
      });
      toast.success(
        `Imported "${data.pack_name}" — ${data.created.boilerplates.length} boilerplates, ` +
        `${data.created.presets.length} presets, ${data.created.workflows.length} workflows, ` +
        `${data.created.mappings.length} mappings`
      );
      setDiff(null);
      onImported?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setCommitting(false); }
  };

  const uploadCustom = async (file) => {
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) { toast.error("Pack file must be under 1MB"); return; }
    setFileBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/starter-packs/import-file", fd);
      toast.success(
        `Imported "${data.pack_name}" — ${data.created.boilerplates.length} boilerplates, ` +
        `${data.created.presets.length} presets, ${data.created.workflows.length} workflows, ` +
        `${data.created.mappings.length} mappings`
      );
      onImported?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setFileBusy(false); }
  };

  if (packs === null) return <div className="h-40 glass animate-pulse rounded-lg" />;

  const iconFor = (id) => {
    if (id.startsWith("saas")) return "#38bdf8";
    if (id.startsWith("health")) return "#10b981";
    if (id.startsWith("financial")) return "#1e40af";
    if (id.startsWith("professional")) return "#8b5cf6";
    return "#9ca3af";
  };

  return (
    <div data-testid="starter-pack-picker">
      <div className={`grid ${compact ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"} gap-3`}>
        {packs.map((p, i) => (
          <motion.button
            key={p.id}
            onClick={() => preview(p)}
            data-testid={`starter-pack-${p.id}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="text-start p-4 rounded-xl border border-[var(--border)] bg-[var(--surface)] hover:border-[var(--accent)] transition group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${iconFor(p.id)}22`, color: iconFor(p.id) }}>
                <Package size={16} weight="duotone" />
              </div>
              <span className="chip chip-neutral text-[10px] uppercase">{p.industry}</span>
            </div>
            <div className="text-sm font-semibold leading-tight mb-1" style={{ fontFamily: "var(--font-heading)" }}>{p.name}</div>
            <div className="text-[11px] text-[var(--text-2)] leading-relaxed line-clamp-3 mb-3">{p.description}</div>
            <div className="grid grid-cols-4 gap-1 text-[10px]">
              <Counter icon={FileText}  label="boilerplates" n={p.counts.boilerplates} />
              <Counter icon={Stack}     label="presets"      n={p.counts.presets} />
              <Counter icon={FlowArrow} label="workflows"    n={p.counts.workflows} />
              <Counter icon={Gavel}     label="mappings"     n={p.counts.mappings} />
            </div>
          </motion.button>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-xs text-[var(--text-3)] cursor-pointer hover:text-[var(--text-2)]">
          <input
            type="file"
            accept=".json,.yaml,.yml,application/json"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && uploadCustom(e.target.files[0])}
            data-testid="starter-pack-upload-input"
          />
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-[var(--border-strong)] hover:border-[var(--accent)] ${fileBusy ? "opacity-60" : ""}`}>
            <Upload size={12} /> {fileBusy ? "Uploading…" : "Upload custom pack (.json/.yaml)"}
          </span>
        </label>
        {allowSkip && onSkip && (
          <Button variant="ghost" onClick={onSkip} data-testid="starter-pack-skip-btn" className="text-[var(--text-3)]">
            Skip for now
          </Button>
        )}
      </div>

      <Dialog open={!!diff} onOpenChange={(v) => !v && setDiff(null)}>
        <DialogContent data-testid="starter-pack-preview-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} weight="duotone" className="text-[var(--accent)]" />
              Preview import · {diff?.pack?.name}
            </DialogTitle>
            <DialogDescription>
              Duplicates (by name) are automatically skipped so re-running the import is safe.
            </DialogDescription>
          </DialogHeader>

          {diff && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {/* Slice toggles — let admins cherry-pick which buckets to import */}
              <div
                className="flex flex-wrap items-center gap-2 text-[11px] text-[var(--text-3)] border border-dashed border-[var(--border)] rounded-md px-3 py-2"
                data-testid="starter-pack-slice-toggles"
              >
                <span className="uppercase tracking-wider font-medium me-1">Import slices:</span>
                {SLICE_KEYS.map((k) => (
                  <label key={k} className="inline-flex items-center gap-1 cursor-pointer select-none capitalize">
                    <input
                      type="checkbox"
                      checked={!!slices[k]}
                      onChange={(e) => setSlices((s) => ({ ...s, [k]: e.target.checked }))}
                      className="accent-[var(--accent)]"
                      data-testid={`starter-pack-slice-${k}`}
                    />
                    {k}
                  </label>
                ))}
              </div>

              {SLICE_KEYS.map((k) => {
                const c = diff.diff[k].create;
                const s = diff.diff[k].skip;
                const disabled = !slices[k];
                return (
                  <GlassCard key={k} className={`p-3 transition ${disabled ? "opacity-40" : ""}`} data-testid={`preview-section-${k}`}>
                    <div className="overline mb-2 capitalize flex items-center justify-between">
                      <span>{k}</span>
                      {disabled && <span className="text-[9px] text-[var(--text-3)] italic">skipped by toggle</span>}
                    </div>
                    {c.length === 0 && s.length === 0 && <div className="text-xs text-[var(--text-3)] italic">Nothing in this pack.</div>}
                    {c.length > 0 && (
                      <div className="mb-1">
                        <div className="text-[10px] text-emerald-400 font-mono uppercase tracking-wider mb-1">+ Create ({c.length})</div>
                        <div className="flex flex-wrap gap-1">
                          {c.map((n) => <span key={n} className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">{n}</span>)}
                        </div>
                      </div>
                    )}
                    {s.length > 0 && (
                      <div>
                        <div className="text-[10px] text-[var(--text-3)] font-mono uppercase tracking-wider mb-1 mt-2 inline-flex items-center gap-1"><Warning size={10} /> Skip — already exists ({s.length})</div>
                        <div className="flex flex-wrap gap-1">
                          {s.map((n) => <span key={n} className="text-[11px] px-1.5 py-0.5 rounded bg-[var(--surface-2)] text-[var(--text-3)] border border-[var(--border)]">{n}</span>)}
                        </div>
                      </div>
                    )}
                  </GlassCard>
                );
              })}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDiff(null)} data-testid="starter-pack-cancel-btn"><X size={14} className="mr-1" /> Cancel</Button>
            <Button onClick={commit} disabled={committing} data-testid="starter-pack-confirm-btn" className="gap-2">
              <CheckCircle size={14} weight="fill" /> {committing ? "Importing…" : "Import pack"} <ArrowRight size={14} />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Counter({ icon: Icon, label, n }) {
  return (
    <div className="flex items-center gap-1 text-[var(--text-3)]" title={label}>
      <Icon size={10} /> <span className="font-mono">{n}</span>
    </div>
  );
}
